import React, { useEffect } from "react";
import WarningIcon from "@material-ui/icons/Warning";
import { useTimeout } from "@sonder/ui/source/hooks/useTimeout";
import { Checkmark, Gear, X } from "@sonder/ui/source/icons";
import {
  StyledToastMessage,
  ToastTitle,
} from "@sonder/ui/source/styled/components/ToastMessage";
import { CircleButton } from "@sonder/ui/source/styled/fragments/CircleButton";
import {
  ErrorList,
  ErrorListItem,
  ErrorMessage,
} from "@sonder/ui/source/styled/fragments/Error";
import { Toast } from "@sonder/ui/source/styled/fragments/Toast";
import { useSegment } from "analytics/segment";
import { THIRTY_SECONDS } from "core/constants";
import {
  FileUploadAttachments,
  clearFileUploadAttachments,
  refetchQueries as refetchQueriesAction,
} from "tasks/actions";
import { useUploadAndAttachFiles } from "tasks/hooks";
import { useStore } from "tasks/store";

interface UploadAttachmentToastProps extends FileUploadAttachments {}

export const UploadAttachmentsToast = ({
  attachableId,
  attachableType,
  files,
}: UploadAttachmentToastProps) => {
  const { dispatch } = useStore();
  const {
    isAttaching,
    isUploading,
    data: { upload: uploadData, attach: attachData },
    error: { upload: uploadError, attach: attachError },
    onUploadAndAttach,
  } = useUploadAndAttachFiles({});
  const { start } = useTimeout(() => {
    dispatch(
      clearFileUploadAttachments({
        attachableId,
        attachableType,
        files,
      }),
    );
  }, THIRTY_SECONDS);
  const hasUploadData = Boolean(uploadData);
  const hasUploadError = Boolean(uploadError);
  const { track } = useSegment();
  useEffect(() => {
    if (!isAttaching && !isUploading && !hasUploadData && !hasUploadError) {
      onUploadAndAttach({
        attachableId,
        attachableType,
        files,
      }).then(resp => {
        dispatch(refetchQueriesAction(["GetTask"]));
        const data = (resp || []).filter(r => r.data?.createAttachment);
        const errors = (resp || []).filter(r => r.errors);
        if (data.length && errors.length === 0) {
          start();
        }
      });
    }
  }, [
    attachableId,
    attachableType,
    isAttaching,
    isUploading,
    hasUploadData,
    hasUploadError,
    ...files,
  ]);

  const total = files.length;
  const remaining = total - attachData.length;
  const errors = [...attachError].filter(e => Boolean(e));
  const hasAttachmentData = attachData.length > 0;
  const hasAttachmentErrors = errors.length > 0;
  const message =
    remaining === 0
      ? "Upload Complete!"
      : `Uploading Files... (${total - remaining}/${total}).`;
  const subtitle =
    !hasAttachmentErrors && remaining === 0
      ? `${total} ${total > 0 ? "files" : "file"} uploaded.`
      : null;
  const errorMessage = hasAttachmentErrors ? (
    <ErrorMessage>
      <WarningIcon />
      {` ${errors.length} ${
        errors.length > 0 ? "files were" : "file was"
      } unsuccessful.`}
    </ErrorMessage>
  ) : null;

  const isCloseDisabled =
    hasUploadError || hasAttachmentErrors ? false : !hasAttachmentData;

  return (
    <Toast>
      {hasAttachmentData ? <Checkmark /> : <Gear />}
      <StyledToastMessage>
        <ToastTitle>{message}</ToastTitle>
        {subtitle && <div>{subtitle}</div>}
        {errorMessage && <div>{errorMessage}</div>}
        {hasUploadError && (
          <ErrorList>
            {uploadError && (
              <ErrorListItem>{uploadError.message}</ErrorListItem>
            )}
            {attachError.length > 0 &&
              attachError.map(error => (
                <ErrorListItem key={error.message}>
                  {error.message}
                </ErrorListItem>
              ))}
          </ErrorList>
        )}
      </StyledToastMessage>
      <CircleButton
        backgroundVariant="dark"
        disabled={isCloseDisabled}
        onClick={() => {
          dispatch(
            clearFileUploadAttachments({
              attachableId,
              attachableType,
              files,
            }),
          );
          track({
            eventName: "upload_attachments_toast",
            element: "dismiss_button",
            action: "click",
            props: {},
          });
        }}
      >
        <X />
      </CircleButton>
    </Toast>
  );
};
